import { useState } from "react";
import axios from "axios";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { fmtDate, fmtMoney, fmtRate } from "./utils";

const th = "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink/45";
const td = "px-4 py-3 text-sm text-ink/85";

export const StatementsTable = ({ statements, token }) => {
  const [downloading, setDownloading] = useState("");

  const download = async (s) => {
    if (downloading) return;
    setDownloading(s.id);
    try {
      const resp = await axios.get(`${API}/admin/statements/${s.id}/file`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = s.filename || "statement";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Original file isn't stored for this upload — only new uploads include the file.");
    } finally {
      setDownloading("");
    }
  };

  return (
    <section data-testid="statements-section" className="rounded-2xl border border-ink/10 bg-surface">
      <div className="border-b border-ink/10 px-5 py-4">
        <h2 className="text-base font-bold text-ink">Statements Analyzed</h2>
        <p className="text-xs text-ink/45">AI-extracted numbers from every uploaded processing statement</p>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="statements-table" className="min-w-full">
          <thead>
            <tr className="border-b border-ink/5">
              <th className={th}>Date</th>
              <th className={th}>File</th>
              <th className={th}>Lead Email</th>
              <th className={th}>Processor</th>
              <th className={th}>Volume</th>
              <th className={th}>Fees</th>
              <th className={th}>Rate</th>
              <th className={th}>Est. Savings</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {statements.map((s, i) => {
              const a = s.analysis || {};
              return (
                <tr key={s.id || i} data-testid={`statement-row-${i}`} className="border-t border-ink/5 transition-colors hover:bg-elevated/40">
                  <td className={`${td} whitespace-nowrap text-ink/55`}>{fmtDate(s.created_at)}</td>
                  <td className={td}>
                    <span className="flex items-center gap-2" title={a.notes || ""}>
                      <FileText className="h-4 w-4 shrink-0 text-mint" />
                      <span className="max-w-[180px] truncate">{s.filename || "statement"}</span>
                    </span>
                  </td>
                  <td className={td}>
                    {s.lead_email ? (
                      <a href={`mailto:${s.lead_email}`} className="text-mint hover:underline">{s.lead_email}</a>
                    ) : (
                      <span className="text-ink/40">anonymous</span>
                    )}
                  </td>
                  <td className={td}>{a.provider || "—"}</td>
                  <td className={`${td} whitespace-nowrap`}>{fmtMoney(a.monthly_volume)}</td>
                  <td className={`${td} whitespace-nowrap`}>{fmtMoney(a.monthly_fees)}</td>
                  <td className={td}>{fmtRate(a.effective_rate)}</td>
                  <td className={`${td} whitespace-nowrap font-semibold text-mint`}>
                    {s.est_savings != null ? `${fmtMoney(s.est_savings)}/mo` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      data-testid={`statement-download-${i}`}
                      onClick={() => download(s)}
                      disabled={downloading === s.id}
                      className="flex items-center gap-1.5 rounded-full border border-mint/40 px-3 py-1.5 text-xs font-semibold text-mint transition-colors hover:bg-mint hover:text-white disabled:opacity-50"
                    >
                      {downloading === s.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                      File
                    </button>
                  </td>
                </tr>
              );
            })}
            {statements.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-sm text-ink/40">
                  No statements uploaded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
